import {
    signUp,
    confirmSignUp,
    resendSignUpCode,
    signIn,
    confirmSignIn,
    signOut,
    fetchAuthSession,
    resetPassword,
    confirmResetPassword,
    updatePassword,
    fetchUserAttributes,
    updateUserAttributes
} from "aws-amplify/auth";
import { sessionStorage } from "aws-amplify/utils";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { Amplify } from "aws-amplify";

import authConfig from "../config/authConfig";
import i18n from "../i18n";
import { deleteAnonymousToken } from "../util/anonymousTokenStorage";

/**
 * Configures the AWS Auth Library with the given configuration
 */
export const configure = () => {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
    return Amplify.configure({
        Auth: {
            Cognito: {
                allowGuestAccess: true,
                userPoolClientId: authConfig.userPoolWebClientId,
                userPoolId: authConfig.userPoolId,
                loginWith: { // Optional
                    oauth: {
                        domain: authConfig.domain,
                        scopes: authConfig.scope,
                        redirectSignIn: authConfig.redirectSignIn,
                        redirectSignOut: authConfig.redirectSignOut,
                        responseType: "code",
                    },
                    username: "true",
                    email: "false", // Optional
                    phone: "false", // Optional
                }
            }
        }
    });
};

export const userSignUp = async ({email, password, name, token}) =>
    signUp({
        username: email,
        password,
        options: {
            userAttributes: {
                name,
                email
            },
            // must include language when overriding default clientMetadata
            validationData: {
                token,
                language: i18n.language
            },
            clientMetadata: {
                token,
                language: i18n.language
            }
        }
    });

/**
 * resendSignUp
 */
export const resendSignUp = username => {
    return resendSignUpCode({username});
};

export const userConfirmSignUp = (username, confirmationCode) => confirmSignUp({username, confirmationCode});

/**
 * Signs in the user to AWS Cognito
 * @param {string} username
 * @param {string} password
 */
export const userSignIn = (username, password, captcha) =>
	signIn({
		username,
		password,
		options: {
			clientMetadata: { token: captcha }
		}
	});

export const userConfirmSignIn = (challengeResponse) => confirmSignIn({challengeResponse: challengeResponse});

/**
 * Signs out the user from Cognito
 */
export const userSignOut = async () => {
    try {
        await signOut({global: true});
    } catch (error) {
        await signOut();
    }
    await sessionStorage.clear();
    deleteAnonymousToken(); // make sure we trigger the auth change event
};

/**
 * Send a request password reset email to the account
 * @param {string} email
 */
export const requestPasswordReset = email => {
    return resetPassword({username: email});
};

/**
 * Submit the users new password given the code that was sent via email
 * @param {string} username The username of the user likely an email
 * @param {string} code The code emailed to the user to verify identity
 * @param {string} newPassword The password the user would like to use
 */
export const forgotPasswordSubmit = (username, code, newPassword) => {
    return confirmResetPassword({username, confirmationCode: code, newPassword});
};

/**
 * Returns a valid idToken for the current user
 * If the token is expired it will be automatically refreshed
 */
export const getToken = async () => {
    try {
        const {tokens} = await fetchAuthSession();
        return tokens.accessToken;
    } catch (error) {
        //ignore
    }
};


/**
 * Allows the currently logged in user to reset their password.
 */
export const userChangePassword = async (currentPassword, newPassword) => {
    return updatePassword({oldPassword: currentPassword, newPassword});
};

export const getCurrentUserAttributes = () => {
    return fetchUserAttributes().catch(() => {/*ignore*/});
};

export const userUpdateAttributes = async attributes => {
    return updateUserAttributes({
        userAttributes: {
            ...attributes
        }
    });
};
